import { useState } from "react";
import { Check, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { useWorkspace } from "@/context/WorkspaceContext";
import { errorMessage } from "@/lib/api";
import { gbp } from "./Credit";

export function PlanPicker({ w, canManage, go, busy, onChanged }) {
  const { api } = useWorkspace();
  const [switching, setSwitching] = useState(null);
  const subscribed = w.has_subscription && !["canceled", "incomplete_expired"].includes(w.status);
  const change = async (p) => {
    if (!window.confirm(`Switch to ${p.name} (${gbp(p.monthly_fee_gbp)}/month + VAT)? The difference for the rest of this month is added to your next invoice.`)) return;
    setSwitching(p.id);
    try { await api.post("/billing/change-plan", { origin_url: window.location.origin, plan_id: p.id }); toast.success(`You're now on ${p.name}`); onChanged(); }
    catch (e) { toast.error(errorMessage(e)); }
    finally { setSwitching(null); }
  };
  return (
    <div className="grid gap-3 sm:grid-cols-2" data-testid="plan-picker">
      {w.plans.map((p) => {
        const current = p.id === w.plan_id && subscribed;
        return (
          <div key={p.id} className={`rounded-lg border p-4 ${p.id === w.plan_id ? "border-primary bg-primary/5" : ""}`} data-testid={`plan-${p.id}`}>
            <p className="font-medium">{p.name}</p>
            <p className="mt-1 font-display text-2xl">{gbp(p.monthly_fee_gbp)}<span className="text-xs text-muted-foreground"> /month + VAT</span></p>
            <ul className="mt-2 space-y-1 text-xs text-muted-foreground">
              <li><Check className="mr-1 inline h-3 w-3" />{gbp(p.included_credit_gbp)} research credit every month</li>
              <li><Check className="mr-1 inline h-3 w-3" />Extra research at {p.markup_pct < 30 ? "a lower" : "the standard"} rate</li>
            </ul>
            {canManage && <div className="mt-3">
              {current ? <span className="text-xs font-medium text-primary" data-testid={`plan-current-${p.id}`}>Your plan</span>
                : subscribed ? <Button size="sm" variant="outline" disabled={!!switching} onClick={() => change(p)} data-testid={`plan-switch-${p.id}`}>{switching === p.id && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />}Switch to {p.name.replace("AJsource ", "")}</Button>
                : <Button size="sm" disabled={!!busy} onClick={() => go(`sub-${p.id}`, "/billing/subscribe", { plan_id: p.id })} data-testid={`plan-subscribe-${p.id}`}>{busy === `sub-${p.id}` && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />}Subscribe</Button>}
            </div>}
          </div>
        );
      })}
    </div>
  );
}

export function AutoTopup({ w, canManage, onChanged }) {
  const { api } = useWorkspace();
  const [f, setF] = useState(w.auto_topup);
  const [busy, setBusy] = useState(false);
  const save = async (next) => {
    setBusy(true);
    try { await api.put("/billing/auto-topup", next); setF(next); toast.success(next.enabled === f.enabled ? "Auto top-up settings saved" : next.enabled ? "Auto top-up is on" : "Auto top-up is off"); onChanged(); }
    catch (e) { toast.error(errorMessage(e)); }
    finally { setBusy(false); }
  };
  const sel = "h-9 rounded-md border bg-background px-2 text-sm";
  return (
    <div className="space-y-3 rounded-lg border p-4" data-testid="auto-topup">
      <div className="flex items-center justify-between gap-3">
        <div><p className="font-medium">Auto top-up</p><p className="text-xs text-muted-foreground">Charges your saved card so research never stops.</p></div>
        {canManage && <Switch checked={f.enabled} disabled={busy} onCheckedChange={(v) => save({ ...f, enabled: v })} data-testid="auto-topup-toggle" />}
      </div>
      <div className="flex flex-wrap items-center gap-2 text-sm">
        When credit drops below
        <select className={sel} value={f.threshold_gbp} disabled={!canManage || busy} onChange={(e) => (f.enabled ? save({ ...f, threshold_gbp: Number(e.target.value) }) : setF({ ...f, threshold_gbp: Number(e.target.value) }))} data-testid="auto-topup-threshold">
          {[2, 5, 10, 20, 50].map((v) => <option key={v} value={v}>£{v}</option>)}
        </select>
        add
        <select className={sel} value={f.pack_gbp} disabled={!canManage || busy} onChange={(e) => (f.enabled ? save({ ...f, pack_gbp: Number(e.target.value) }) : setF({ ...f, pack_gbp: Number(e.target.value) }))} data-testid="auto-topup-pack">
          {w.topup_packs_gbp.map((v) => <option key={v} value={v}>£{v} + VAT</option>)}
        </select>
      </div>
      {w.auto_topup.last_error && <p className="text-xs text-destructive" data-testid="auto-topup-error">{w.auto_topup.last_error}</p>}
      {w.auto_topup.last_topup_at && <p className="text-xs text-muted-foreground" data-testid="auto-topup-last">Last auto top-up {new Date(w.auto_topup.last_topup_at).toLocaleString("en-GB")}</p>}
    </div>
  );
}
